import {
  deliveryServiceAtom,
  merchantStatusAtom,
  takeoutServiceAtom,
} from "@/atoms"
import { indoorServiceAtom } from "@/atoms/indoor"
import merchantAccessStateAtom from "@/atoms/merchant-access"
import { minuteNowAtom, tenantTimezoneAtom } from "@/atoms/tenant-clock"
import { tableAtom } from "@/atoms/table"
import {
  getEarliestNextOpening,
  getNextOpening,
} from "@/lib/get-next-opening"
import { isServiceOpen } from "@/lib/is-service-open"
import { getLatestCurrentClosing, isNowInSchedule } from "@/lib/schedule"
import type { ServiceType } from "@/types"
import { useAtomValue } from "jotai"
import { useMemo } from "react"

/**
 * Open/closed status for the storefront the customer is browsing.
 *
 * Product rules:
 * - `?table=` → INDOOR only (hours + menu of the indoor service).
 * - Without table → shared **DELIVERY catalog/menu** for delivery AND takeout.
 *   - Browsing/add-to-cart is allowed when **DELIVERY or TAKEOUT** is open
 *     (restaurants often run delivery until X and takeout from Y with the same
 *     menu).
 *   - Checkout still gates each option by that channel's own hours
 *     (TAKEOUT uses takeout service hours; DELIVERY uses delivery service hours).
 *   - TAKEOUT never switches the catalog to a takeout-only menuId.
 *
 * `/menu` is out of scope (not the table/indoor order flow).
 */
export function useMerchantStatus() {
  const table = useAtomValue(tableAtom)
  const merchantStatus = useAtomValue(merchantStatusAtom)
  const accessState = useAtomValue(merchantAccessStateAtom)
  const deliveryService = useAtomValue(deliveryServiceAtom)
  const takeoutService = useAtomValue(takeoutServiceAtom)
  const indoorService = useAtomValue(indoorServiceAtom)
  const timeZone = useAtomValue(tenantTimezoneAtom)
  const now = useAtomValue(minuteNowAtom)

  const storeToggledOff = merchantStatus === "UNAVAILABLE"

  const accountSuspended = accessState === "SUSPENDED"

  const channel: ServiceType = table ? "INDOOR" : "DELIVERY"

  const merchantIsOpen = useMemo(() => {
    if (accountSuspended) return false
    if (storeToggledOff) return false
    if (table) return isServiceOpen(indoorService, now, timeZone)
    return isServiceOpen(deliveryService, now, timeZone) || isServiceOpen(takeoutService, now, timeZone)
  }, [accountSuspended, storeToggledOff, table, indoorService, deliveryService, takeoutService, timeZone, now])

  const closedWithinSchedule = useMemo(() => {
    if (merchantIsOpen) return false
    if (table) {
      return (
        Boolean(indoorService?.serviceHours?.weekHours?.length) &&
        isNowInSchedule(indoorService!.serviceHours.weekHours, now, timeZone)
      )
    }
    const deliveryIn =
      Boolean(deliveryService?.serviceHours?.weekHours?.length) &&
      isNowInSchedule(deliveryService!.serviceHours.weekHours, now, timeZone)
    const takeoutIn =
      Boolean(takeoutService?.serviceHours?.weekHours?.length) &&
      isNowInSchedule(takeoutService!.serviceHours.weekHours, now, timeZone)
    return deliveryIn || takeoutIn
  }, [merchantIsOpen, table, indoorService, deliveryService, takeoutService, timeZone, now])

  const nextOpening = useMemo(() => {
    if (accountSuspended) return null
    if (merchantIsOpen || closedWithinSchedule) return null
    if (table) {
      if (!indoorService?.serviceHours?.weekHours) return null
      return getNextOpening(indoorService.serviceHours.weekHours, now, timeZone)
    }
    return getEarliestNextOpening(
      [deliveryService?.serviceHours?.weekHours, takeoutService?.serviceHours?.weekHours],
      now,
      timeZone,
    )
  }, [accountSuspended, merchantIsOpen, closedWithinSchedule, table, indoorService, deliveryService, takeoutService, timeZone, now])

  const nextOpeningMessage = useMemo(() => {
    if (!nextOpening) return null
    return `Abre ${nextOpening.dayLabel} às ${nextOpening.time}`
  }, [nextOpening])

  const closingTime = useMemo(() => {
    if (!merchantIsOpen) return null
    if (table) {
      return getLatestCurrentClosing({
        schedules: [indoorService?.serviceHours?.weekHours],
        now,
        timeZone,
      })
    }
    return getLatestCurrentClosing({
      schedules: [
        deliveryService?.serviceHours?.weekHours,
        takeoutService?.serviceHours?.weekHours,
      ],
      now,
      timeZone,
    })
  }, [merchantIsOpen, table, indoorService, deliveryService, takeoutService, timeZone, now])

  return {
    merchantIsOpen,
    nextOpeningMessage,
    closingTime,
    nextOpening,
    channel,
    /** Convenience for checkout/UI: DELIVERY channel hours only. */
    deliveryIsOpen:
      !accountSuspended && !storeToggledOff && isServiceOpen(deliveryService, now, timeZone),
    /** Convenience for checkout/UI: TAKEOUT channel hours only (same menu as delivery). */
    takeoutIsOpen:
      !accountSuspended && !storeToggledOff && isServiceOpen(takeoutService, now, timeZone),
    /**
     * The account is suspended. Exposed so copy can distinguish "closed now"
     * from "not operating", and so nothing offers a reopening estimate for it.
     */
    accountSuspended,
  }
}
